// ADR 23.9.26w §7 — the drainer: one pass at a time, only for the signed-in
// owner, stopping per kind the way the phones' four drainers each stop at
// their own first kept failure.
//
// Its one decision is rust-core's (§2): `statusDiscardsQueuedOp` over the
// response's status, `0` for "no response arrived". Everything else here is a
// loop over fetch and IndexedDB. ADR 25.9.26f §3 — the facade is reached
// through `import('../../wasm')` inside the drain, never statically: the shell
// imports this module through `useOfflineQueue`, and a static edge would hold
// the signed-out first paint for the wasm download.
//
// - **Single-flight, twice over.** In the tab, one `inFlight` promise; a
//   trigger during a pass sets `rerun`, so an op put mid-pass (a coalesced
//   replacement) goes out without waiting for an unrelated trigger. Across
//   tabs, `navigator.locks` with `ifAvailable` — a tab that finds it held
//   skips. Without `navigator.locks` only the in-tab guard applies, which is
//   still safe: PUTs are absolute and reviews carry their key.
// - **The owner, before every send.** A sign-out or an account switch
//   mid-pass aborts it without sending, and W2's purge does the same.
// - **Backoff.** A pass that kept anything with a status arms one timer —
//   30 s, doubling to a 15-minute cap, `Retry-After` honoured — because a tab
//   can sit open and online for hours with no other trigger. A status-0 pass
//   arms nothing: W2's re-check produces the online transition that retries.

import { onlineManager } from '@tanstack/react-query'

import { ApiError } from '../../api/http'
import { opDiscarded, queueDrained } from './analytics'
import { currentOwner, listRecords, refreshMirror, removeIfRev, setNotice } from './queue'
import { sendOp } from './send'
import { compareOps, isQueuedOp, type OpKind, type QueuedOp, type StoredRecord } from './types'

const BACKOFF_BASE_SECS = 30
const BACKOFF_CAP_SECS = 900
const LOCK_NAME = 'lingara-offline-queue'
const CONFLICT = 409

export interface DrainerDeps {
  /** The signed-in user, re-read before every send. */
  owner: () => string | null
  online: () => boolean
  /** A pass sent or discarded something: server truth replaces the overlay. */
  onChanged: () => void
  locks: () => LockManager | undefined
}

const defaults: DrainerDeps = {
  owner: currentOwner,
  online: () => onlineManager.isOnline(),
  onChanged: () => undefined,
  locks: () => (typeof navigator === 'undefined' ? undefined : navigator.locks),
}
let deps: DrainerDeps = defaults

let inFlight: Promise<void> | null = null
let rerun = false
let step = BACKOFF_BASE_SECS
let timer: ReturnType<typeof setTimeout> | null = null
let armedSecs: number | null = null
// Bumped by a purge: a pass started under an older generation stops sending.
let generation = 0

interface Tally {
  attempted: number
  sent: number
  discarded: number
  kept: number
  stoppedStatus: number | null
  keptWithStatus: boolean
  retryAfter: number
  blocked: Set<OpKind>
  /** No response arrived: the next request would fail the same way. */
  ended: boolean
}

function newTally(): Tally {
  return {
    attempted: 0, sent: 0, discarded: 0, kept: 0, stoppedStatus: null,
    keptWithStatus: false, retryAfter: 0, blocked: new Set(), ended: false,
  }
}

/** Replace some of the drainer's inputs. `useOfflineQueue` wires `onChanged`; suites wire the rest. */
export function configureDrainer(next: Partial<DrainerDeps>): void {
  deps = { ...deps, ...next }
}

function cancelTimer(): void {
  if (timer !== null) clearTimeout(timer)
  timer = null
  armedSecs = null
}

/** W2's purge: abort any pass, cancel the backoff, forget the step. Stored ops stay (§8). */
export function resetDrainer(): void {
  generation += 1
  rerun = false
  step = BACKOFF_BASE_SECS
  cancelTimer()
}

/** Test seam: the armed backoff in seconds, or `null`. */
export function armedBackoffSecs(): number | null {
  return armedSecs
}

/** Test seam: back to the module's initial state, `deps` included. */
export function resetDrainerForTests(): void {
  resetDrainer()
  inFlight = null
  deps = defaults
}

const statusOf = (error: unknown) => (error instanceof ApiError ? error.status : 0)

/**
 * ADR 25.9.26f §3 — rust-core's decision, behind the dynamic import. A facade
 * that cannot answer (a failed init, which `main.tsx` is already refusing)
 * keeps the op rather than rejecting into the `void requestDrain()` triggers:
 * a queued write is never discarded on a guess.
 */
async function discards(status: number): Promise<boolean> {
  try {
    const { statusDiscardsQueuedOp } = await import('../../wasm')
    return statusDiscardsQueuedOp(status)
  } catch {
    return false
  }
}

async function settleFailure(op: QueuedOp, error: unknown, tally: Tally): Promise<void> {
  const status = statusOf(error)
  if (await discards(status)) {
    tally.discarded += 1
    await removeIfRev(op.userId, op.key, op.rev)
    opDiscarded(op.kind, status)
    // §9 — the one discard the learner is told about, in the server's words.
    if (op.kind === 'list_pin' && status === CONFLICT && error instanceof ApiError) setNotice(error.message)
    return
  }
  tally.kept += 1
  tally.stoppedStatus ??= status
  if (status === 0) {
    tally.ended = true
    return
  }
  tally.keptWithStatus = true
  tally.blocked.add(op.kind)
  const retryAfter = error instanceof ApiError ? error.retryAfterSecs : undefined
  if (retryAfter !== undefined) tally.retryAfter = Math.max(tally.retryAfter, retryAfter)
}

async function attempt(op: QueuedOp, tally: Tally): Promise<void> {
  tally.attempted += 1
  try {
    await sendOp(op)
  } catch (error) {
    await settleFailure(op, error, tally)
    return
  }
  tally.sent += 1
  // A record the learner replaced while this send was in flight has a new
  // `rev`, so it survives here and the re-run pass sends it.
  await removeIfRev(op.userId, op.key, op.rev)
}

/** §4 — a record that fails the kind guard is deleted and captured, never sent. */
async function readableOps(records: StoredRecord[], tally: Tally): Promise<QueuedOp[]> {
  const readable: QueuedOp[] = []
  for (const record of records) {
    if (isQueuedOp(record)) {
      readable.push(record)
      continue
    }
    tally.discarded += 1
    await removeIfRev(record.userId, record.key, record.rev)
    opDiscarded('unreadable', 0)
  }
  return readable.sort(compareOps)
}

async function pass(userId: string, startedIn: number): Promise<Tally> {
  const tally = newTally()
  const ops = await readableOps(await listRecords(userId), tally)
  for (const op of ops) {
    if (tally.ended) break
    if (tally.blocked.has(op.kind)) continue
    if (startedIn !== generation || deps.owner() !== userId) break
    await attempt(op, tally)
  }
  return tally
}

function arm(secs: number): void {
  cancelTimer()
  armedSecs = secs
  timer = setTimeout(() => {
    timer = null
    armedSecs = null
    void requestDrain()
  }, secs * 1000)
}

function finish(tally: Tally): void {
  if (tally.attempted > 0) {
    const { sent, discarded, kept, stoppedStatus } = tally
    queueDrained({ sent, discarded, kept, stopped_status: stoppedStatus })
  }
  if (tally.sent + tally.discarded > 0) deps.onChanged()
  void refreshMirror()
  // The step resets only after a pass that kept nothing with a status: a 2xx
  // on another kind beside one stuck review does not reset it.
  if (!tally.keptWithStatus) {
    step = BACKOFF_BASE_SECS
    return
  }
  arm(Math.max(step, tally.retryAfter))
  step = Math.min(step * 2, BACKOFF_CAP_SECS)
}

async function underLock(work: () => Promise<void>): Promise<void> {
  const locks = deps.locks()
  if (locks === undefined) return work()
  await locks.request(LOCK_NAME, { ifAvailable: true }, async (lock) => {
    if (lock !== null) await work()
  })
}

const guard = () => deps.online() && deps.owner() !== null

async function loop(): Promise<void> {
  do {
    rerun = false
    const userId = deps.owner()
    if (userId === null) return
    const startedIn = generation
    await underLock(async () => finish(await pass(userId, startedIn)))
  } while (rerun && guard())
}

/**
 * Every trigger's one entry point. Offline or signed out it does nothing and
 * arms nothing — the drainer never calls `authed()` offline, so a flaky
 * network cannot start a refresh behind it. Any trigger cancels an armed
 * backoff and drains at once.
 */
export function requestDrain(): Promise<void> {
  cancelTimer()
  if (!guard()) return Promise.resolve()
  if (inFlight !== null) {
    rerun = true
    return inFlight
  }
  const running = loop().finally(() => {
    inFlight = null
  })
  inFlight = running
  return running
}
